import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FiSave, FiX, FiGlobe, FiStar } from 'react-icons/fi';
// useAuth imported for potential future use and context access
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import { dashboardAPI } from '../../services/realApi';
import CustomizableDashboard from './CustomizableDashboard';
import { getTemplatesForContext } from './widgetRegistry';

interface Props {
  dashboardId?: string | null;
  onSave?: (data: unknown) => void;
  onCancel?: () => void;
}

const DashboardDesigner: React.FC<Props> = ({
  dashboardId: propDashboardId = null,
  onSave,
  onCancel
}) => {
  useAuth(); // Initialize auth context
  const { selectedClientId } = useClient();
  const navigate = useNavigate();
  const { dashboardId: routeDashboardId } = useParams<{ dashboardId: string }>();
  const [searchParams] = useSearchParams();

  const dashboardId = routeDashboardId || propDashboardId;

  const fromDashboardId = searchParams.get('from');
  const templateKey = searchParams.get('template');
  const [dashboardName, setDashboardName] = useState('');
  const [dashboardDescription, setDashboardDescription] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [context, setContext] = useState('hub');
  const [layout, setLayout] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (dashboardId) {
      loadDashboard(dashboardId, false);
    } else if (fromDashboardId) {
      loadDashboard(fromDashboardId, true);
    } else if (templateKey) {
      loadTemplate(templateKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, fromDashboardId, templateKey]);

  const loadDashboard = async (id: string, isDuplicating = false): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await dashboardAPI.getDashboard(id);
      if (result.success && result.data) {
        const dashboard = result.data as {
          name: string;
          description?: string;
          context?: string;
          is_published?: boolean;
          is_default?: boolean;
          layout?: unknown;
        };

        if (isDuplicating) {
          setDashboardName(`${dashboard.name} (Copy)`);
          setIsPublished(false);
          setIsDefault(false);
        } else {
          setDashboardName(dashboard.name);
          setIsPublished(dashboard.is_published ?? false);
          setIsDefault(dashboard.is_default ?? false);
        }

        setDashboardDescription(dashboard.description || '');
        setContext(dashboard.context || 'hub');

        const layoutData = (dashboard.layout as Record<string, unknown>) || {};
        const layoutArray = Array.isArray(layoutData)
          ? layoutData
          : (layoutData.lg as unknown[]) || (layoutData.default as unknown[]) || [];
        setLayout(layoutArray);
      } else {
        setError(result.error || 'Failed to load dashboard');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = (key: string): void => {
    setLoading(true);
    setError(null);
    try {
      const templates = getTemplatesForContext(context) as Record<string, {
        name: string;
        description?: string;
        widgets?: unknown[];
      }>;
      const template = templates[key];

      if (template) {
        setDashboardName(`${template.name} (Custom)`);
        setDashboardDescription(template.description || '');
        setLayout(template.widgets || []);
        setIsPublished(false);
        setIsDefault(false);
      } else {
        setError(`Template "${key}" not found`);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!dashboardName.trim()) {
      setError('Dashboard name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const widgets = (layout as Array<{ i: string }>).map(item => {
        const parts = item.i.split('-');
        const widgetId = parts.length > 2 && !Number.isNaN(Number(parts[parts.length - 1]))
          ? parts.slice(0, -1).join('-')
          : item.i;

        return {
          widget_id: widgetId,
          layout_item_id: item.i,
          config: {}
        };
      });

      const layoutDict = layout.length > 0 ? { lg: layout } : {};

      const dashboardData = {
        name: dashboardName.trim(),
        description: dashboardDescription.trim() || null,
        is_published: isPublished,
        is_default: isDefault,
        context: context,
        layout: layoutDict,
        widgets: widgets
      };

      let result;
      if (dashboardId) {
        result = await dashboardAPI.updateDashboard(dashboardId, dashboardData);
      } else {
        result = await dashboardAPI.createDashboard(dashboardData, selectedClientId);
      }

      if (result.success) {
        if (onSave) {
          onSave(result.data);
        } else {
          navigate('/hub/dashboard');
        }
      } else {
        setError(result.error || 'Failed to save dashboard');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleLayoutChange = (newLayout: unknown): void => {
    if (Array.isArray(newLayout)) {
      setLayout(newLayout);
    } else if (newLayout && (newLayout as Record<string, unknown>).lg) {
      setLayout((newLayout as Record<string, unknown[]>).lg);
    } else {
      setLayout([]);
    }
  };

  const getTitle = (): string => {
    if (dashboardId) return 'Edit Dashboard';
    if (fromDashboardId) return 'Duplicate Dashboard';
    if (templateKey) return 'Clone View';
    return 'Create Dashboard';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header with form */}
      <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {getTitle()}
          </h1>
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel || (() => navigate('/hub/dashboard'))}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <FiX className="w-4 h-4" />
              <span>Cancel</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !dashboardName.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiSave className="w-4 h-4" />
              <span>{saving ? 'Saving...' : 'Save Dashboard'}</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="dashboard-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Dashboard Name *
            </label>
            <input id="dashboard-name"
              type="text"
              value={dashboardName}
              onChange={(e) => setDashboardName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
              placeholder="My Custom Dashboard"
            />
          </div>

          <div>
            <label htmlFor="context" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Context
            </label>
            <select id="context"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
            >
              <option value="hub">Hub</option>
              <option value="genaiops">GenAIOps</option>
              <option value="agentops">AgentOps</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea id="description"
              value={dashboardDescription}
              onChange={(e) => setDashboardDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
              placeholder="Describe what this dashboard is for..."
            />
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <FiGlobe className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Publish to organization</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                disabled={!isPublished}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
              />
              <FiStar className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Set as default</span>
            </label>
          </div>
        </div>
      </div>

      {/* Dashboard preview/editor */}
      <div className="mt-8">
        <CustomizableDashboard
          title={dashboardName || 'Dashboard Preview'}
          description={dashboardDescription || 'Build your dashboard by adding widgets'}
          storageKey={`dashboard-designer-${dashboardId || (fromDashboardId ? 'duplicate-' + fromDashboardId : 'new')}`}
          context={context}
          defaultLayout={layout.length > 0 ? layout : []}
          onLayoutChange={handleLayoutChange}
          noPadding={true}
        />
      </div>
    </div>
  );
};

export default DashboardDesigner;
